// src/components/Logo.tsx

import React from 'react';
import styled from 'styled-components';
import { Link } from '@/utils/router';
import { useTheme } from '@/theme/ThemeProvider';
import { type SeoCity } from '@/constants/geo';

import { MEDIA_BREAKPOINTS } from '@/constants/layout';

const LOGO_DARK_URL = 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/1.png';
const LOGO_LIGHT_URL = 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/2.png';
const LOGO_DARK_WEBP_480_URL = 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/1-480.webp';
const LOGO_DARK_WEBP_960_URL = 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/1-960.webp';
const LOGO_LIGHT_WEBP_480_URL = 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/2-480.webp';
const LOGO_LIGHT_WEBP_960_URL = 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/2-960.webp';
const LOGO_WEBP_WIDTH = 480;
const LOGO_WEBP_HEIGHT = 67;
const BUSINESS_NAME = 'די ג׳יי אליאור מור';

const LogoImage = styled.img`
  aspect-ratio: ${LOGO_WEBP_WIDTH} / ${LOGO_WEBP_HEIGHT};
  width: ${({ theme }) => theme.sizes.small.logoWidth};
  height: auto;
  display: block;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    width: ${({ theme }) => theme.sizes.medium.logoWidth};
    height: auto;
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
    width: ${({ theme }) => theme.sizes.large.logoWidth};
    height: auto;
  }
`;

type Props = {
  decorative?: boolean;
  keyword?: string;
  city?: SeoCity;
};

const Logo: React.FC<Props> = ({ decorative = true, keyword, city }) => {
  const { mode } = useTheme();

  // 1=light, 2=dark (as you described)
  const logoSrc = mode === 'light' ? LOGO_LIGHT_URL : LOGO_DARK_URL;
  const logoSrcSet =
    mode === 'light'
      ? `${LOGO_LIGHT_WEBP_480_URL} 480w, ${LOGO_LIGHT_WEBP_960_URL} 960w, ${LOGO_LIGHT_URL} 1800w`
      : `${LOGO_DARK_WEBP_480_URL} 480w, ${LOGO_DARK_WEBP_960_URL} 960w, ${LOGO_DARK_URL} 1800w`;

  const defaultAlt = BUSINESS_NAME;
  const contextualAlt = keyword && city ? `${BUSINESS_NAME} – ${keyword} ב${city}` : defaultAlt;
  const imgAlt = decorative ? '' : contextualAlt;

  return (
    <Link to="/" aria-label={`${BUSINESS_NAME} – דף הבית`}>
      <LogoImage
        src={logoSrc}
        srcSet={logoSrcSet}
        sizes="(max-width: 768px) 250px, 350px"
        alt={imgAlt}
        aria-hidden={decorative ? 'true' : undefined}
        width={LOGO_WEBP_WIDTH}
        height={LOGO_WEBP_HEIGHT}
        loading="eager"
        decoding="async"
        draggable={false}
        onDragStart={(e) => e.preventDefault()} 
        onContextMenu={(e) => e.preventDefault()}
      />
    </Link>
  );
};

export default Logo;
